
import { Component, Watch } from 'vue-property-decorator';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { Project, ProjectUser } from '@/model/Project';
import ToastVariant from '@/model/enums/toast/toast-variant';
import ToastMixin from '@/mixins/ToastMixin';
import { mixins } from 'vue-class-component';
import FormInput from '@/components/form/FormInput.vue';
import InviteButton from '@/active-components/customer-project/InviteButton.vue';
import ProjectRole from '@/model/enums/ProjectRole';
import { ProfileResponse } from '@/model/Profile';
import ProjectTable from '@/components/customer-project/ProjectTable.vue';
import FormSelector from '@/components/form/FormSelector.vue';
import { createFromEnum, FormItemWithClass } from '@/model/form/FormItem';
import Package from '@/model/enums/Package';
import { UpdateForm } from '@/model/form/UpdateForm';
import ConfirmationModal from '@/components/modal/ConfirmationModal.vue';
import {
    EventResponse,
    InviteUserRequest,
    RemoveUserRequest,
} from '@/model/Event';

@Component({
    components: {
        FormInput,
        FormSelector,
        InviteButton,
        ProjectTable,
        ConfirmationModal,
    },
    methods: {
        ...mapActions({
            deactivate: 'customerProjectStorage/deactivate',
            reactivate: 'customerProjectStorage/reactivate',
            getAllProjects: 'customerProjectStorage/getAllProjects',
            fetchProject: 'customerProjectStorage/fetchProject',
            getAllProjectUsers: 'customerProjectStorage/getAllProjectUsers',
            removeUserFromProject:
                'customerProjectStorage/removeUserFromProject',
            changeRole: 'customerProjectStorage/changeRole',
            fetchProfile: 'profileStorage/fetchProfile',
            updatePackage: 'customerProjectStorage/updatePackage',
            inviteUser: 'eventStorage/inviteUser',
            removeUser: 'eventStorage/removeUser',
            fetchProjectEvents: 'customerProjectStorage/fetchProjectEvents',
        }),
        ...mapMutations({
            setProject: 'customerProjectStorage/setProject',
            setProjectEvents: 'customerProjectStorage/setProjectEvents',
        }),
    },
    computed: {
        ...mapGetters({
            projectUsers: 'customerProjectStorage/getProjectUsers',
            projects: 'customerProjectStorage/getProjects',
            selectedProject: 'customerProjectStorage/getProject',
            profile: 'profileStorage/getProfile',
            projectEvents: 'customerProjectStorage/getProjectEvents',
        }),
    },
})
export default class CustomerProject extends mixins(ToastMixin) {
    selectedPackage?: Package | null = null;

    selectedProject!: Project | null;

    loading = false;

    projectUsers!: ProjectUser[];

    profile!: ProfileResponse | null; // kellhet még

    projectId = +this.$route.params.id;

    fetchProfile!: () => Promise<void | ProfileResponse>; // kellhet még

    fetchProject!: (projectId: number) => Promise<void | Project>;

    setProject!: (project: Project | null) => void;

    getAllProjectUsers!: (id: number) => Promise<void>; // kell a filter miatt

    getAllProjects!: (onlyActives: boolean) => Promise<void>;

    deactivate!: (id: number | null) => Promise<void>;

    reactivate!: (id: number | null) => Promise<void>;

    fetchProjectEvents!: (projectId: number) => Promise<void | EventResponse[]>;

    setProjectEvents!: (events: EventResponse[]) => void;

    projectEvents!: Array<EventResponse>;

    updatePackage!: ({
        id,
        formData: newPackage,
    }: UpdateForm<Package>) => Promise<void>;

    changeRole!: (newRole: {
        userId: number;
        projectId: number;
        role: string;
    }) => Promise<void>;

    removeUserFromProject!: ({
        projectId,
        userId,
    }: {
        projectId: number;
        userId: number;
    }) => Promise<void | number>;

    inviteUser!: (inviteUserRequest: InviteUserRequest) => Promise<void>;

    removeUser!: (removeUserRequest: RemoveUserRequest) => Promise<void>;

    mounted(): void {
        this.loading = true;
        this.setProjectEvents([]);
        Promise.all([
            this.fetchProject(this.projectId).then(() => {
                this.selectedPackage = this.selectedProject?.projectPackage;
            }),
            this.getAllProjectUsers(this.projectId),
            this.fetchProfile(),
            this.fetchProjectEvents(this.projectId),
        ]).finally(() => {
            this.loading = false;
        });
    }

    get packages(): FormItemWithClass<Package | null>[] {
        return createFromEnum<Package | null>(Package, 'package.type.');
    }

    @Watch('selectedPackage')
    onSelectedPlanChange(): void {
        if (
            this.selectedPackage &&
            this.selectedProject &&
            this.selectedProject.projectPackage !== this.selectedPackage
        ) {
            const oldPackage = this.selectedProject.projectPackage;
            this.updatePackage({
                id: this.selectedProject.id,
                formData: this.selectedPackage,
            }).catch(() => {
                this.selectedPackage = oldPackage;
            });
        }
    }

    onClickDeactivate(): void {
        this.loading = true;
        if (this.selectedProject) {
            this.deactivate(this.selectedProject.id)
                .then(() => {
                    if (this.selectedProject)
                        this.selectedProject.enabled = false;
                    this.showToast(
                        this.$t('customer-project.success').toString(),
                        this.$t('success-title').toString(),
                        ToastVariant.SUCCESS,
                    );
                    this.$bvModal.hide('deactivate-modal');
                })
                .catch(() => {
                    this.showToast(
                        this.$t(
                            this.$store.getters[
                                'customerProjectStorage/getError'
                            ].key,
                        ).toString(),
                        this.$t('customer-project.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    onClickReactivate(): void {
        this.loading = true;
        if (this.selectedProject) {
            this.reactivate(this.selectedProject.id)
                .then(() => {
                    if (this.selectedProject)
                        this.selectedProject.enabled = true;
                    this.showToast(
                        this.$t('customer-project.success').toString(),
                        this.$t('success-title').toString(),
                        ToastVariant.SUCCESS,
                    );
                    this.$bvModal.hide('reactivate-modal');
                })
                .catch(() => {
                    this.showToast(
                        this.$t(
                            this.$store.getters[
                                'customerProjectStorage/getError'
                            ].key,
                        ).toString(),
                        this.$t('customer-project.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    onRemove(userId: number): void {
        this.loading = true;
        if (this.selectedProject?.id) {
            this.removeUserFromProject({
                userId,
                projectId: this.selectedProject.id,
            })
                .then(() => {
                    this.showToast(
                        this.$t('remove-user.success').toString(),
                        this.$t('success-title').toString(),
                        ToastVariant.SUCCESS,
                    );
                    this.$bvModal.hide(`remove-user-modal${userId}`);
                })
                .catch(() => {
                    this.showToast(
                        this.$t(
                            this.$store.getters[
                                'customerProjectStorage/getError'
                            ].key,
                        ).toString(),
                        this.$t('remove-user.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    onChange({ role, id }: { role: ProjectRole; id: number }): void {
        if (!this.selectedProject?.id) {
            return;
        }
        const projectId = this.selectedProject.id;
        this.loading = true;
        this.changeRole({
            userId: id,
            projectId,
            role,
        })
            .then(() => {
                this.showToast(
                    this.$t('customer-project.role-change.success').toString(),
                    this.$t('success-title').toString(),
                    ToastVariant.SUCCESS,
                );
            })
            .catch(() => {
                this.showToast(
                    this.$t(
                        this.$store.getters['customerProjectStorage/getError']
                            .key,
                    ).toString(),
                    this.$t('invite.error-title').toString(),
                    ToastVariant.DANGER,
                );
                return this.getAllProjectUsers(projectId).catch(() => {
                    return Promise.resolve();
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    goBack(): void {
        this.setProject(null);
        this.$router.push('/customer-projects');
    }

    onSelectEvent(request: InviteUserRequest): void {
        this.inviteUser(request);
    }

    onRemoveEvent(request: RemoveUserRequest): void {
        this.removeUser(request);
    }
}
